import React, { useEffect, useMemo, useState } from "react";
import { ApiConstants } from "../../constants/ApiConstants";
import useMakeRequest from "../../utils/ApiHelper";
import { PAGINATION } from "../../constants/Constants";
import { useNavigate } from "react-router-dom";
import BrandedLoaderOverlay from "../layout/BrandedLoaderOverlay";
import { useResourceEverLoaded } from "../layout/useSmartBrandedLoader";
import "../layout/page-loading-shell.scss";
import { LanguageConstants } from "../../constants/LanguageConstants";
import { toast } from "react-toastify";
import { ProfileAction } from "../../redux/Action/Authaction";
import { useDispatch, useSelector } from "react-redux";
import InlineLoadingButton from "../layout/InlineLoadingButton";
import "./category-select.scss";

const formatCategoryLabel = (value: any): string => {
  const source = String(value ?? "").trim();
  if (!source) return "";
  return source
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

/** API / DB sometimes return category ids as strings; keep selection in sync with list rows. */
const numId = (value: any): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : NaN;
};

function UpdateCateory() {
  const [data, setData] = useState<any[]>([]);
  const categoryList = useMakeRequest();
  const categoryAdd = useMakeRequest();
  const navigate = useNavigate();
  const [select_ids, setSelect_ids] = useState<any[]>([]);
  const [initialCategoryIds, setInitialCategoryIds] = useState<number[]>([]);
  const dispatch = useDispatch();
  const profiledata = useSelector((state: any) => state?.ProfileState);
  const [pagination, setPagination] = useState({
    page: PAGINATION.PAGE || 0,
    pageSize: PAGINATION.PAGESIZE || 10,
  });

  useEffect(() => {
    const payload = {
      page: pagination?.page,
      pageSize: pagination?.pageSize,
    };
    categoryList.makeRequest({
      payload,
      url: ApiConstants?.HomeCategory,
      toastStatus: false,
    });
  }, [pagination]);

  useEffect(() => {
    if (categoryList.status) {
      setData(categoryList?.data?.data?.rows || []);
    }
  }, [categoryList?.status]);

  useEffect(() => {
    if (
      categoryList?.data?.data?.count >= 10 &&
      pagination?.pageSize < categoryList?.data?.data?.count
    ) {
      const handleScroll = () => {
        if (
          window.innerHeight + window.scrollY >=
          document.body.offsetHeight - 100
        ) {
          setPagination((prevPagination) => ({
            ...prevPagination,
            pageSize:
              prevPagination.pageSize < 20 ? 20 : prevPagination.pageSize,
          }));
        }
      };
      window.addEventListener("scroll", handleScroll);
      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    }
  }, [pagination, categoryList?.data?.data?.count]);

  const handleSelect = (id: any) => {
    const nid = numId(id);
    if (Number.isNaN(nid)) return;
    setSelect_ids((prev: any[]) => {
      const normalizedPrev = prev.map((x: any) => numId(x)).filter((x) => !Number.isNaN(x));
      const has = normalizedPrev.includes(nid);
      if (has) {
        if (normalizedPrev.length === 1) {
          toast.error("At least one category must be selected");
          return normalizedPrev;
        }
        return normalizedPrev.filter((item: number) => item !== nid);
      }
      return [...normalizedPrev, nid];
    });
  };

  useEffect(() => {
    if (!categoryAdd?.status) return;
    // Reload profile so `user_category` matches DB (otherwise sync effect keeps old selection).
    // @ts-ignore
    dispatch(ProfileAction());
    const t = window.setTimeout(() => navigate("/"), 400);
    return () => window.clearTimeout(t);
  }, [categoryAdd?.status, dispatch, navigate]);

  useEffect(() => {
    if (!profiledata?.data?.data || !(data?.length > 0)) return;
    const rows = profiledata?.data?.data?.user_category;
    const profileIdsRaw = Array.isArray(rows)
      ? rows.map((item: any) => numId(item?.cat_id)).filter((n: number) => !Number.isNaN(n))
      : [];
    const categoryIdSet = new Set(
      data.map((item: any) => numId(item?.id)).filter((n: number) => !Number.isNaN(n)),
    );
    const validIds = profileIdsRaw.filter((id: number) => categoryIdSet.has(id));
    setSelect_ids(validIds);
    setInitialCategoryIds([...validIds].sort((a, b) => a - b));
  }, [profiledata?.data?.data, data]);

  const normalizedSelection = useMemo(
    () =>
      select_ids
        .map((x: any) => numId(x))
        .filter((n: number) => !Number.isNaN(n))
        .sort((a: number, b: number) => a - b),
    [select_ids],
  );

  const hasCategoryChanges = useMemo(() => {
    if (initialCategoryIds.length !== normalizedSelection.length) return true;
    return initialCategoryIds.some((id, index) => id !== normalizedSelection[index]);
  }, [initialCategoryIds, normalizedSelection]);

  const isSaving = categoryAdd.loading;

  const handelSubmit = () => {
    if (isSaving || !hasCategoryChanges) return;
    if (select_ids?.length === 0) {
      toast.error(LanguageConstants?.CATEGORY_SELECT_ERROR);
      return;
    }
    const payload = {
      cat_ids: select_ids.map((x: any) => numId(x)).filter((n: number) => !Number.isNaN(n)),
      type: "update",
    };
    categoryAdd.makeRequest({ payload, url: ApiConstants?.AddCategory, toastStatus: true });
  };

  const isCategorySelected = (rowId: any) =>
    select_ids.some((sid: any) => numId(sid) === numId(rowId));

  const categoryListEverLoaded = useResourceEverLoaded(categoryList?.data?.data != null);
  const initialCategoryPending =
    !categoryListEverLoaded && categoryList.error == null && !categoryList.status;
  const showCategoryLoader =
    isSaving ||
    initialCategoryPending ||
    (categoryList.loading && !categoryListEverLoaded);
  const showPageContent = categoryListEverLoaded || categoryList.error != null;

  return (
    <>
      <BrandedLoaderOverlay active={showCategoryLoader} immediate />
      {showPageContent ? (
      <main className="category-select-main page-loading-shell__content">
        <div className="footer-space">
          <div className="category-select-shell">
            <div className="category-select-card">
              <div className="category-select-card__hero category-select-card__hero--manage">
                <div className="category-select-card__titles">
                  <h1 className="category-select-card__title">
                    {LanguageConstants?.CATEGORY_SELECTION}
                  </h1>
                  <p className="category-select-card__sub">
                    {LanguageConstants?.SELECT_CATEGORY_SUBLINE_1}
                  </p>
                </div>
              </div>

              <div className="category-select-card__body">
                <div className="category-select-grid">
                  {data.length > 0 ? (
                    data.map((res: any, i: number) => (
                      <button
                        type="button"
                        key={res?.id ?? i}
                        className={
                          isCategorySelected(res?.id)
                            ? "category-select-pill is-active"
                            : "category-select-pill"
                        }
                        onClick={() => handleSelect(res?.id)}
                      >
                        {formatCategoryLabel(res?.category_details?.name)}
                      </button>
                    ))
                  ) : (
                    <p className="category-select-grid__empty">NO DATA</p>
                  )}
                </div>

                <div className="category-select-card__actions">
                  <InlineLoadingButton
                    type="button"
                    className="category-select-continue"
                    loading={isSaving}
                    onClick={handelSubmit}
                    disabled={!hasCategoryChanges || categoryAdd.status}
                  >
                    {LanguageConstants?.SAVE}
                  </InlineLoadingButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      ) : null}
    </>
  );
}

export default UpdateCateory;
